import styled from '@emotion/styled';
import { sdsColor, sdsRadius } from '@staging-design-system/tokens';
import { rem } from '@utils';

const GroupList = styled.ol`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden auto;
  padding-inline: ${rem(8)};
  touch-action: manipulation;
  scrollbar-color: ${sdsColor.background.defaultTertiary} transparent;
  scrollbar-width: thin;
  width: 100%;
`;

const GroupListItem = styled.li`
  list-style-type: none;
`;

const Menu = styled.menu<{ isOpen?: boolean }>(
  ({ isOpen }) => `
  display: flex;
  align-self: stretch;
  flex-direction: column;
  row-gap: ${rem(8)};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding: 0;
  align-self: stretch;
  border-bottom: ${isOpen ? `1px solid ${sdsColor.border.defaultDefault}` : 'none'};

  > li {
    align-items: ${isOpen ? 'stretch' : 'center'};
  }
`
);

const MenuItem = styled.li<{
  active: boolean;
  hidden?: boolean;
  isOpen?: boolean;
}>(
  ({ active, hidden, isOpen }) => `
  display: ${hidden ? 'none' : 'flex'};
  flex-flow: column;
  list-style-type: none;
  min-width: 0;
  overflow: hidden;
  width: 100%;

  flex: ${active ? '0 1 auto' : '0 0 auto'};
  min-height: 0;

  & > button,
  & > a {
    box-sizing: border-box;
    margin-inline: ${rem(8)};
    margin-bottom: ${active ? rem(12) : 0};
    max-width: ${isOpen ? `calc(100% - ${rem(16)})` : 'none'};
    width: ${isOpen ? `calc(100% - ${rem(16)})` : 'auto'};
  }

  ${GroupList} {
    display: ${active ? 'block' : 'none'};
    max-height: ${active ? '100%' : 0};
    opacity: ${active ? 1 : 0};
    transition:
      opacity 150ms ease-in-out ${active ? 150 : 0}ms,
      max-height 0ms ${active ? 0 : 150}ms;
  }
`
);

const GroupTitle = styled.div(
  ({ theme }) => `
  color: ${theme.palette.grey['600']};
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(16)};
  padding: ${rem(8)};
`
);

const Group = styled.ol();

const GroupItem = styled.li<{ active?: boolean }>(
  ({ active }) => `
  color: ${active ? sdsColor.text.brandDefault : sdsColor.text.defaultDefault};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: ${rem(14)};
  font-weight: 400;
  opacity: 1;
  padding: ${rem(8)};
  position: relative;
  user-select: none;
  border-radius: ${sdsRadius.xSmall};

  &::before {
    background: ${sdsColor.text.brandDefault};
    border-radius: ${sdsRadius.xSmall} 0 0 ${sdsRadius.xSmall};
    bottom: 0;
    content: '';
    left: 0;
    opacity: ${active ? 1 : 0};
    pointer-events: none;
    position: absolute;
    top: 0;
    width: ${rem(1)};
  }

  &::after {
    background: linear-gradient(
      to right, transparent 82%, var(--sidebar-background-color) 92%
    );
    border-radius: inherit;
    content: '';
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  
  &:not(:focus-visible, :focus-within, :hover) {
    transition: 
      opacity 200ms linear,
      padding-top 200ms ease-in-out 200ms,
      padding-bottom 200ms ease-in-out 200ms,
      padding-right 0ms 200ms,
      height 200ms ease-in-out 200ms,
      transform 0ms 200ms;
  }

  &.active {
    color: ${sdsColor.text.brandDefault};
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    ${GroupList}:not(.disable-active) &:active {
      background: var(--menu-group-item-highlight-background-color);
      color: ${
        active ? sdsColor.text.brandDefault : sdsColor.text.defaultDefault
      };
      transition: 0ms 25ms;
      
      &::after {
        display: none;
      }
    }
  }

  // Only on non-touch devices
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${
        active ? 'transparent' : sdsColor.background.defaultDefaultHover
      };
      color: ${
        active ? sdsColor.text.brandDefault : sdsColor.text.defaultDefault
      };
      transition: none;

      &::after {
        background: linear-gradient(
          to right,
          transparent 68%,
          ${
            active
              ? 'var(--sidebar-background-color)'
              : sdsColor.background.defaultDefaultHover
          } 80%
        );
      }
    }
  }

  &:focus-within {
    transition: 
      opacity 200ms linear,
      padding-right 0ms ease-in-out 200ms,
      height 200ms ease-in-out 200ms,
      transform 0ms 200ms;
  }

  &:is(:hover, .active, :focus-within) {
    padding-right: ${rem(16)};
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    padding-right: ${rem(16)};

    &::after {
      background: linear-gradient(
        to right, 
        transparent 68%, 
        var(--sidebar-background-color) 80%
      );    
    }    

    &.active {
      background: var(--menu-group-item-highlight-background-color);
    }

    &.active::after {
      background: linear-gradient(
        to right,
        transparent 68%,
        var(--menu-group-item-highlight-background-color) 80%
      );
    }
  }

  &:focus-visible {
    outline: 2px solid ${sdsColor.border.brandSecondary};
    outline-offset: -2px;
  }

  &.deleted {
    height: 0;
    opacity: 0;
    padding: 0 ${rem(24)};
    pointer-events: none;
    transform: scale(0);
  }
`
);

const GroupItemLink = styled.a`
  position: absolute;
  inset: 0;
`;

const GroupItemText = styled.span`
  flex: 1 0 100%;
  overflow: hidden;
  white-space: nowrap;

  ${GroupItem}.active & {
    // flex: 1 1 auto;
  }

  // Only on non-touch devices
  @media (hover: hover) and (pointer: fine) {
    ${GroupItem}:is(:hover, :has(*.action-menu-active)) & {
      flex: 1 1 auto;
    }
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    ${GroupItem} & {
      flex: 1 1 auto;
    }
  }
`;

const ActionMenuWrapper = styled.div`
  padding-left: ${rem(8)};
  z-index: 1;    

  .action-menu {
    background: transparent;
    color: var(--dropdown-menu-icon-color);
    transform: rotateZ(90deg);
    visibility: hidden;

    svg {
      fill: var(--dropdown-menu-icon-color);
    }

    &:hover {
      background: ${({ theme }) => theme.palette.primary.main};

      svg {
        fill: ${({ theme }) => theme.palette.common.white};
      }
    }
  }

  // Only on non-touch devices
  @media (hover: hover) and (pointer: fine) {
    ${GroupItem}:hover & .action-menu {
      visibility: visible;
    }    
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    ${GroupItem} & .action-menu {
      visibility: visible;
    }
  }
  
  .action-menu-active {
    background: ${({ theme }) => theme.palette.primary.dark};
    visibility: visible;
    
    svg {
      fill: ${({ theme }) => theme.palette.common.white};
    }
  }
`;

const Loading = styled.div<{ hidden?: boolean }>`
  display: flex;
  justify-content: center;
  padding: ${rem(16)} 0 ${rem(24)};
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
`;

export default {
  ActionMenuWrapper,
  Group,
  GroupItem,
  GroupItemLink,
  GroupItemText,
  GroupList,
  GroupListItem,
  GroupTitle,
  Loading,
  Menu,
  MenuItem
};
